import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TenantProvisioningStatusDTO } from '@today/types'
import { tenantProvisioningService } from '@/services/tenant-provisioning.service'

const keyOf = (tenantId: string) => ['tenant-provisioning', tenantId] as const

/**
 * A tenant's provisioning status and its retry (TO-375). After an accepted
 * retry the status is fetched again, so the reset (PENDING) rows show.
 */
export function useTenantProvisioning(tenantId: string) {
	const client = useQueryClient()
	const query = useQuery<TenantProvisioningStatusDTO>({
		queryKey: keyOf(tenantId),
		queryFn: () => tenantProvisioningService.getStatus(tenantId),
	})
	const retry = useMutation({
		mutationFn: () => tenantProvisioningService.retry(tenantId),
		onSuccess: () => client.invalidateQueries({ queryKey: keyOf(tenantId) }),
	})
	return {
		status: query.data ?? null,
		isLoading: query.isLoading,
		loadFailed: query.isError,
		reload: () => void query.refetch(),
		retry: () => retry.mutate(),
		retrying: retry.isPending,
		retryFailed: retry.isError,
	}
}
